import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Plus, Trash2, Edit2, CreditCard, CheckCircle2, XCircle } from 'lucide-react'
import { toast } from 'sonner'
import { paymentGatewaysApi } from '@/lib/api'
import { getApiErrorMessage } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import type { PaymentGatewaySettings, GatewayType } from '@/types'
import { usePermissions } from '@/contexts/PermissionContext'

const GATEWAY_OPTIONS: { value: GatewayType; label: string }[] = [
  { value: 'STRIPE', label: 'Stripe' },
  { value: 'RAZORPAY', label: 'Razorpay' },
  { value: 'PAYPAL', label: 'PayPal' },
]

export default function PaymentGatewaySettingsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { canAddEdit } = usePermissions()
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [gatewayToDelete, setGatewayToDelete] = useState<PaymentGatewaySettings | null>(null)

  const { data: gateways = [], isLoading } = useQuery({
    queryKey: ['payment-gateways'],
    queryFn: paymentGatewaysApi.getAll,
  })

  const deleteMutation = useMutation({
    mutationFn: paymentGatewaysApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-gateways'] })
      toast.success(t('payments.gatewayDeleted', 'Payment gateway removed'))
      setDeleteDialogOpen(false)
      setGatewayToDelete(null)
    },
    onError: (error) => toast.error(getApiErrorMessage(error, t('payments.deleteError', 'Failed to remove gateway'))),
  })

  const getGatewayInfo = (type: GatewayType) => GATEWAY_OPTIONS.find(g => g.value === type)
  const configuredTypes = gateways.map(g => g.gateway_type)
  const availableTypes = GATEWAY_OPTIONS.filter(g => !configuredTypes.includes(g.value))

  if (isLoading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <CreditCard className="h-6 w-6" />
            {t('payments.gatewaySettings', 'Payment Gateway Settings')}
          </h1>
          <p className="text-muted-foreground">
            {t('payments.gatewaySettingsDescription', 'Configure payment gateways to accept online payments')}
          </p>
        </div>
        {availableTypes.length > 0 && canAddEdit('PAYMENT_GATEWAYS') && (
          <Button onClick={() => navigate('/settings/payment-gateways/new')}>
            <Plus className="h-4 w-4 mr-2" />
            {t('payments.addGateway', 'Add Gateway')}
          </Button>
        )}
      </div>

      {gateways.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <CreditCard className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">{t('payments.noGateways', 'No Payment Gateways Configured')}</h3>
            <p className="text-muted-foreground text-center mb-4">
              {t('payments.noGatewaysDescription', 'Add a payment gateway to start accepting online payments for your invoices.')}
            </p>
            {canAddEdit('PAYMENT_GATEWAYS') && (
              <Button onClick={() => navigate('/settings/payment-gateways/new')}>
                <Plus className="h-4 w-4 mr-2" />
                {t('payments.addFirstGateway', 'Add Your First Gateway')}
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {gateways.map((gateway) => {
            const info = getGatewayInfo(gateway.gateway_type)
            return (
              <Card key={gateway.settings_id} className={gateway.is_enabled ? 'border-green-500/50' : ''}>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">
                      {gateway.display_name || info?.label}
                    </CardTitle>
                    {canAddEdit('PAYMENT_GATEWAYS') && (
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="icon" onClick={() => navigate(`/settings/payment-gateways/${gateway.settings_id}/edit`)}>
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => { setGatewayToDelete(gateway); setDeleteDialogOpen(true) }}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    )}
                  </div>
                  <CardDescription>{info?.label} Payment Gateway</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Status</span>
                    {gateway.is_enabled ? (
                      <Badge variant="default" className="bg-green-500">
                        <CheckCircle2 className="h-3 w-3 mr-1" /> Enabled
                      </Badge>
                    ) : (
                      <Badge variant="secondary">
                        <XCircle className="h-3 w-3 mr-1" /> Disabled
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Mode</span>
                    <Badge variant={gateway.is_test_mode ? 'outline' : 'default'}>
                      {gateway.is_test_mode ? 'Test Mode' : 'Live Mode'}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">API Key</span>
                    <Badge variant={gateway.has_api_key ? 'default' : 'destructive'}>
                      {gateway.has_api_key ? 'Configured' : 'Missing'}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Webhook Secret</span>
                    <Badge variant={gateway.has_webhook_secret ? 'default' : 'secondary'}>
                      {gateway.has_webhook_secret ? 'Configured' : 'Not Set'}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Payment Gateway?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the {gatewayToDelete?.display_name || getGatewayInfo(gatewayToDelete?.gateway_type || 'STRIPE')?.label} gateway configuration.
              You will no longer be able to accept payments through this gateway.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => gatewayToDelete && deleteMutation.mutate(gatewayToDelete.settings_id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? 'Removing...' : 'Remove Gateway'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

